import { type FC, useEffect, useMemo, useRef } from "react";

import { Alert, Button, Card, Flex, Skeleton, Stack } from "@mantine/core";
import { useWindowScroll } from "@mantine/hooks";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, ExclusiveLensStatus, PayoutSortBy, PayoutSortOrder } from "../../../gql-generated/types";
import { useLens } from "../../../lens/hooks";
import { PayoutDriversCard } from "../../../lens-creator-payout/components/PayoutDriversCard";
import { RemovedExclusiveLensAlert } from "../../../lens-creator-payout/components/RemovedExclusiveLensAlert";
import { PayoutTosBanner } from "../../../shared/components/tos";
import { makePayoutDateRange } from "../../converters";
import { useLensIdSearchParameter, usePayoutStatsLensList, usePayoutStatsSummary, useTimeRange } from "../../hooks";
import { LensGranularity } from "../../models";
import { getLensPlusExclusivityCutoff } from "../../utils/lensPlusDateRanges";

import {
    breakdownSection,
    chartSection,
    container,
    filterBanner,
    tableSection,
} from "./PayoutMonetizationTab.module.css";
import { ClearSelection, ShowingSelectedLens } from "./messages.generated";
import { CountryBreakdownChart, MonthlyPayoutsChart } from "./payout-history";
import { RewardedLensesTable } from "./payout-lenses";

const FILTER_BANNER_HEIGHT = 44;
const SCROLL_DELAY = 100;

const LENS_LIST_PARAMS = {
    countryFilters: [],
    sortBy: PayoutSortBy.SortByTotalAmountMilliUsd,
    sortOrder: PayoutSortOrder.SortOrderDesc,
    limit: 200,
};

export const PayoutMonetizationTab: FC = () => {
    const [, scrollTo] = useWindowScroll();
    const { configs, loading: configLoading } = useFeatureGates();
    const isMonetizationInsightsEnabled = configs[CofConfigId.MyLensesMonetizationInsights].enabled;
    const { lensId: selectedLensId, clearLensId } = useLensIdSearchParameter();
    const { data: selectedLensData, loading: selectedLensLoading } = useLens(selectedLensId ?? "", !selectedLensId);
    const prevLensIdRef = useRef(selectedLensId);

    useEffect(() => {
        if (selectedLensId !== prevLensIdRef.current) {
            prevLensIdRef.current = selectedLensId;
            const timeout = setTimeout(() => {
                scrollTo({ y: 0 });
            }, SCROLL_DELAY);
            return () => {
                clearTimeout(timeout);
            };
        }
        return;
    }, [scrollTo, selectedLensId]);
    const selectedLens = selectedLensData?.getLens.lens;
    const selectedLensName = selectedLens?.name;
    const selectedUnlockableId = selectedLens?.unlockableId.toString();
    const isResolvingSelectedLens = !!selectedLensId && selectedLensLoading;
    // Default to Lifetime here so creators with only stale payouts see their
    // earnings; URL param still wins. Lens+ drivers apply their own exclusivity
    // cutoff while generic payout history keeps this creator-selected range.
    const { selectedTimeRange } = useTimeRange({ defaultRange: LensGranularity.Lifetime });
    const baseDateRange = useMemo(() => makePayoutDateRange(selectedTimeRange), [selectedTimeRange]);
    const { startDate, endDate } = baseDateRange;
    const isRemovedFromLensPlus = selectedLens?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusRemoved;
    const lensPlusCutoff = getLensPlusExclusivityCutoff(
        selectedLens?.exclusiveLensStatus,
        selectedLens?.markedAsExclusiveAt,
    );

    const lensListResult = usePayoutStatsLensList({
        startDate,
        endDate,
        ...LENS_LIST_PARAMS,
        skip: false,
    });

    const { data: summaryData, loading: summaryLoading } = usePayoutStatsSummary({
        startDate,
        endDate,
        countryFilters: [],
        unlockableId: selectedUnlockableId,
        skip: isResolvingSelectedLens,
    });

    return (
        <Stack className={container}>
            <PayoutTosBanner />
            {selectedLensId &&
                (selectedLensLoading ? (
                    <Skeleton h={FILTER_BANNER_HEIGHT} w="100%" />
                ) : (
                    <Alert className={filterBanner} p="xs" radius={0}>
                        <span className="paragraph-p200">
                            <ShowingSelectedLens lensName={<strong>{selectedLensName}</strong>} />
                        </span>{" "}
                        <Button variant="subtle" size="compact-xs" onClick={clearLensId}>
                            <ClearSelection />
                        </Button>
                    </Alert>
                ))}
            {isRemovedFromLensPlus && <RemovedExclusiveLensAlert />}
            {!isRemovedFromLensPlus && (
                <PayoutDriversCard
                    unlockableId={selectedUnlockableId}
                    timeRange={selectedTimeRange}
                    markedAsExclusiveAt={lensPlusCutoff}
                    isResolvingSelectedLens={isResolvingSelectedLens}
                />
            )}
            <Flex gap="md" direction={{ base: "column", xs: "row" }}>
                <Card shadow="xs" radius={0} p="md" className={chartSection}>
                    <MonthlyPayoutsChart
                        startDate={startDate}
                        endDate={endDate}
                        unlockableId={selectedUnlockableId}
                        skip={isResolvingSelectedLens}
                        isResolvingSelectedLens={isResolvingSelectedLens}
                    />
                </Card>
                {isMonetizationInsightsEnabled && (
                    <Card shadow="xs" radius={0} p="md" className={breakdownSection}>
                        <CountryBreakdownChart
                            loading={summaryLoading || configLoading || isResolvingSelectedLens}
                            countryBreakdown={summaryData?.countryBreakdown ?? []}
                        />
                    </Card>
                )}
            </Flex>

            <Card shadow="xs" radius={0} p="md" className={tableSection}>
                <RewardedLensesTable
                    lenses={lensListResult.lenses}
                    loading={lensListResult.loading}
                    hasMore={lensListResult.hasMore}
                    onLoadMore={lensListResult.fetchMore}
                />
            </Card>
        </Stack>
    );
};
